import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { withDevtools } from '@ngrx-toolkit/core';
import { of, pipe, timer } from 'rxjs';
import { debounce, tap } from 'rxjs/operators';
import { ApiService } from 'src/api';
import { TeamModel } from 'src/app/features/scheduler/models/team.model';
import { withApiOperation } from './features/with-api-operation.feature';
import { withRequestStatus } from './features/with-request-status.feature';

const TEAMS_SEARCH_DEBOUNCE = 1000;

interface TeamsState {
  teams: TeamModel[];
  alreadyLoadedIds: string[];
}

const initialState: TeamsState = {
  teams: [],
  alreadyLoadedIds: [],
};

export const TeamsStore = signalStore(
  withDevtools('teams'),
  withRequestStatus(),
  withApiOperation(),
  withState(() => initialState),
  withComputed(store => ({
    teamsGroupByClub: computed(() =>
      store.teams().reduce(
        (acc: { [key: string]: { [key: string]: TeamModel } }, curr) => ({
          ...acc,
          [curr.clubId]: acc[curr.clubId]
            ? { ...acc[curr.clubId], [curr.id]: curr }
            : { [curr.id]: curr },
        }),
        {},
      ),
    ),
  })),
  withMethods(store => {
    const apiClient = inject(ApiService);

    const loadTeams = async (clubId: string) => {
      if (store.alreadyLoadedIds().includes(clubId)) {
        return;
      }

      await store.runApiOperation({
        request: apiClient.teamsGet({ clubId }),
        onSuccess: teams => {
          patchState(store, state => {
            const existingIds = new Set(state.teams.map(team => team.id));
            return {
              teams: [
                ...state.teams,
                ...teams
                  .map(team => ({ ...team, clubId }))
                  .filter(team => !existingIds.has(team.id)),
              ],
              alreadyLoadedIds: [...state.alreadyLoadedIds, clubId],
            };
          });
          store.setLoaded();
        },
        onError: error => store.handleApiError(error),
      });
    };

    return {
      teamName: (id: string) =>
        store.teams().find(team => team.id === id)?.name ?? '',
      loadingClub: (id: string) =>
        store.loading() && !store.alreadyLoadedIds().includes(id),
      getTeams: rxMethod<{ id: string; forceSearch: boolean }>(
        pipe(
          tap(() => store.setLoading()),
          debounce(({ forceSearch }) =>
            !forceSearch ? timer(TEAMS_SEARCH_DEBOUNCE) : of({}),
          ),
          tap(({ id }) => loadTeams(id)),
        ),
      ),
    };
  }),
);
